import { GAME_CONFIG } from "../config/gameConfig.js";
import { SPRITEFUSION_SPRITE, tileGidToFrame } from "../systems/spritefusionMap.js";
import { getRoomAt, getNearestRoom } from "../systems/rooms.js";
import { setLight, removeLight } from "../systems/lighting.js";
import { queueTextbox, queueTextboxSequence, isTextboxOpen } from "../ui/textboxUI.js";
import { isLoaded, removeModulesCompletely, unlockModules } from "../systems/memory.js";
import { loadGame, saveGame } from "../core/saveSystem.js";

const TILE_W = GAME_CONFIG.level.tileWidth;
const TILE_H = GAME_CONFIG.level.tileHeight;
const PROP_Z = 9505;
const FRAGMENT_PICKUP_Z = 9706;

const SHELF_PROP_TILES = [
    { cell: { x: 41, y: 47 }, gid: 52 },
    { cell: { x: 41, y: 48 }, gid: 53 },
];
const LOCKER_PROP_TILES = [
    { cell: { x: 43, y: 48 }, gid: 54 },
    { cell: { x: 43, y: 49 }, gid: 55 },
];
const LOCKER_INDICATOR_CELL = { x: 43, y: 48 };
const LOCKER_DOOR_CELL = { x: 43, y: 50 };
const LOCKER_REWARD_CELL = { x: 43, y: 51 };
const LOCKER_INTERACT_RADIUS = 34;
const FRAGMENT_COLLECT_RADIUS = 22;
const KEYCARD_MODULE_IDS = ["keycard_crate", "keycard_shelf", "keycard_toilet"];

const FRAGMENTS = [
    { id: "crate", moduleId: "keycard_crate", cell: { x: 32, y: 50 }, offset: { x: -6, y: 0 }, label: "inside the crate", noPickup: true },
    { id: "shelf", moduleId: "keycard_shelf", cell: { x: 41, y: 49 }, offset: { x: 0, y: -6 }, label: "behind the shelf" },
    { id: "toilet", moduleId: "keycard_toilet", cell: { x: 35, y: 45 }, offset: { x: 0, y: 0 }, label: "the toilet", noPickup: true },
];
const LATCH_SAVE_KEY = "ramLatch";

let fragmentsCollected = new Set();
let lockerDoorBlock = null;
let lockerUnlocked = false;
let lockerApproachShown = false;
let lockerDoorSprite = null;
let lockerIndicatorGlow = null;
let keycardsConsumed = false;

function cellCenter(cell) {
    return vec2(cell.x * TILE_W + TILE_W / 2, cell.y * TILE_H + TILE_H / 2);
}

function findFragmentDef(id) {
    return FRAGMENTS.find((fragment) => fragment.id === id);
}

function loadLatchState() {
    const data = loadGame()?.[LATCH_SAVE_KEY];
    return {
        collectedIds: Array.isArray(data?.collectedIds) ? data.collectedIds : [],
        keycardsConsumed: !!data?.keycardsConsumed,
    };
}

function saveLatchState() {
    const existing = loadGame() || {};
    saveGame({
        ...existing,
        [LATCH_SAVE_KEY]: {
            collectedIds: Array.from(fragmentsCollected),
            keycardsConsumed,
        },
    });
}

function restoreCollectedKeycardsToStorage() {
    if (keycardsConsumed) return;
    const moduleIds = FRAGMENTS
        .filter((def) => fragmentsCollected.has(def.id))
        .map((def) => def.moduleId);
    unlockModules(moduleIds);
}

function collectFragment(def) {
    if (fragmentsCollected.has(def.id)) return;
    fragmentsCollected.add(def.id);
    unlockModules([def.moduleId]);
    saveLatchState();

    queueTextbox({
        tag: "SYS // SALVAGE",
        channel: "R5_LATCH",
        text: `Keycard recovered from ${def.label}. (${fragmentsCollected.size}/${FRAGMENTS.length})`,
        variant: "system",
    });

    if (def.id === "toilet") {
        queueTextbox({
            tag: "SYS // DISGUST",
            channel: "R5_LATCH",
            text: "gross...",
            variant: "system",
        });
    }

    maybeUnlockLockerFromMemory();
}

function remainingCount() {
    return FRAGMENTS.length - fragmentsCollected.size;
}

function loadedKeycardCount() {
    return KEYCARD_MODULE_IDS.filter((id) => isLoaded(id)).length;
}

function maybeUnlockLockerFromMemory() {
    if (lockerUnlocked) return;
    if (loadedKeycardCount() < KEYCARD_MODULE_IDS.length) return;
    unlockLocker();
}

function addPropTile(map, cell, gid) {
    const scaleX = TILE_W / map.tileWidth;
    const scaleY = TILE_H / map.tileHeight;
    return add([
        sprite(SPRITEFUSION_SPRITE, { frame: tileGidToFrame(map, gid) }),
        pos(cell.x * TILE_W, cell.y * TILE_H),
        anchor("topleft"),
        scale(scaleX, scaleY),
        z(PROP_Z),
        "ram_latch_prop",
    ]);
}

function createShelfProp(map) {
    for (const { cell, gid } of SHELF_PROP_TILES) {
        addPropTile(map, cell, gid);
    }
}

function applyLockerLight() {
    const indicatorPos = cellCenter(LOCKER_INDICATOR_CELL);
    const room = getRoomAt(indicatorPos) || getNearestRoom(indicatorPos);
    setLight("ram_latch_locker", {
        pos: indicatorPos,
        radius: lockerUnlocked ? 90 : 46,
        color: lockerUnlocked ? [110, 255, 170] : [220, 60, 60],
        intensity: lockerUnlocked ? 0.85 : 0.4,
        pulse: lockerUnlocked,
        flicker: !lockerUnlocked,
        roomId: room ? room.id : null,
    });
}

function lockLockerDoor() {
    if (lockerDoorBlock?.exists?.()) return;
    lockerDoorBlock = add([
        rect(TILE_W, TILE_H),
        pos(LOCKER_DOOR_CELL.x * TILE_W, LOCKER_DOOR_CELL.y * TILE_H),
        anchor("topleft"),
        opacity(0),
        area(),
        body({ isStatic: true }),
        "locker_door_block",
    ]);
}

function unlockLockerDoor() {
    if (lockerDoorBlock?.exists?.()) {
        destroy(lockerDoorBlock);
    }
    lockerDoorBlock = null;
}

function createLocker(map) {
    lockerDoorSprite = null;
    for (const { cell, gid } of LOCKER_PROP_TILES) {
        const propSprite = addPropTile(map, cell, gid);
        if (cell.x === LOCKER_DOOR_CELL.x && cell.y === LOCKER_DOOR_CELL.y) {
            lockerDoorSprite = propSprite;
        }
    }

    const indicatorPos = cellCenter(LOCKER_INDICATOR_CELL);
    lockerIndicatorGlow = add([
        circle(4),
        pos(indicatorPos.x - 7, indicatorPos.y - 5),
        anchor("center"),
        color(255, 70, 70),
        opacity(0.9),
        z(PROP_Z + 1),
    ]);

    if (lockerUnlocked && lockerDoorSprite) {
        lockerDoorSprite.color = rgb(150, 255, 190);
    }
    lockerApproachShown = lockerUnlocked;

    if (!lockerUnlocked) {
        lockLockerDoor();
    }

    applyLockerLight();
}

function unlockLocker() {
    if (lockerUnlocked) return;
    lockerUnlocked = true;
    lockerApproachShown = true;
    applyLockerLight();

    if (lockerDoorSprite?.exists?.()) {
        lockerDoorSprite.color = rgb(150, 255, 190);
    }
    if (lockerIndicatorGlow?.exists?.()) {
        lockerIndicatorGlow.color = rgb(90, 255, 150);
    }

    queueTextboxSequence([
        {
            tag: "SYS // LATCH",
            channel: "R5_LATCH",
            text: "Three keycards seated. The locker's latch clicks and releases.",
            variant: "system",
        },
        {
            tag: "MEMORY // FRAGMENT",
            channel: "R5_LATCH",
            text: "The door is now free to open.",
            variant: "memory",
        },
    ]);
    unlockLockerDoor();
}

function updateLockerProximity() {
    if (lockerUnlocked || lockerApproachShown) return;
    const player = get("player")[0];
    if (!player || isTextboxOpen()) return;
    if (player.pos.dist(cellCenter(LOCKER_DOOR_CELL)) > LOCKER_INTERACT_RADIUS) return;

    lockerApproachShown = true;
    const remaining = remainingCount();
    const seated = loadedKeycardCount();
    queueTextbox({
        tag: "OBJ_SCAN // STORAGE_LATCH",
        channel: "R5_LATCH",
        text: `Locked. A RAM-powered latch — ${seated}/${KEYCARD_MODULE_IDS.length} keycards seated. ${remaining} still missing.`,
        variant: "locked",
    });
}

function maybeConsumeKeycardsAfterDoor() {
    if (!lockerUnlocked || keycardsConsumed) return;
    const player = get("player")[0];
    if (!player?.exists?.()) return;
    const doorExitY = (LOCKER_DOOR_CELL.y + 1) * TILE_H;
    if (player.pos.y < doorExitY) return;

    keycardsConsumed = true;
    removeModulesCompletely(KEYCARD_MODULE_IDS);
    saveLatchState();
}

function spawnFragment(def) {
    if (fragmentsCollected.has(def.id)) return;

    const offsetVec = vec2(def.offset.x, def.offset.y);
    const center = cellCenter(def.cell).add(offsetVec);
    const lightId = `ram_fragment_${def.id}`;
    const room = getRoomAt(center) || getNearestRoom(center);

    const pickup = add([
        pos(center),
        anchor("center"),
        area({
            isSensor: true,
            shape: new Rect(vec2(-16, -16), 32, 32),
        }),
        z(FRAGMENT_PICKUP_Z),
        "ram_fragment_pickup",
        { collected: false, age: 0 },
    ]);

    const glow = pickup.add([
        circle(16),
        anchor("center"),
        color(170, 70, 255),
        opacity(0.22),
        z(FRAGMENT_PICKUP_Z - 1),
    ]);

    pickup.add([
        sprite("keycard"),
        anchor("center"),
        scale(0.9),
        z(FRAGMENT_PICKUP_Z),
    ]);

    function collect() {
        if (pickup.collected) return;
        pickup.collected = true;
        removeLight(lightId);
        destroy(pickup);
        collectFragment(def);
    }

    pickup.onUpdate(() => {
        if (!pickup.exists()) return;
        pickup.age += dt();
        const pulse = 0.5 + Math.sin(pickup.age * 6) * 0.5;
        glow.opacity = 0.16 + pulse * 0.12;

        setLight(lightId, {
            pos: pickup.pos,
            radius: 58 + pulse * 8,
            color: [176, 76, 255],
            intensity: 0.6 + pulse * 0.15,
            pulse: false,
            roomId: room ? room.id : null,
        });

        const player = get("player")[0];
        if (player?.exists?.() && player.pos.dist(pickup.pos) <= FRAGMENT_COLLECT_RADIUS) {
            collect();
        }
    });

    pickup.onDestroy(() => removeLight(lightId));
}

export function collectRamLatchFragmentById(fragmentId) {
    const def = findFragmentDef(fragmentId);
    if (!def) return false;
    collectFragment(def);
    return true;
}

export function createRamLatchPuzzle(map) {
    const savedState = loadLatchState();
    fragmentsCollected = new Set(savedState.collectedIds);
    keycardsConsumed = savedState.keycardsConsumed;
    lockerUnlocked = keycardsConsumed || loadedKeycardCount() >= KEYCARD_MODULE_IDS.length;
    lockerApproachShown = lockerUnlocked;
    lockerDoorSprite = null;
    lockerIndicatorGlow = null;
    lockerDoorBlock = null;
    if (!keycardsConsumed) {
        restoreCollectedKeycardsToStorage();
    }

    createShelfProp(map);
    createLocker(map);
    for (const def of FRAGMENTS) {
        if (!def.noPickup) {
            spawnFragment(def);
        }
    }
    onUpdate(() => {
        maybeUnlockLockerFromMemory();
        updateLockerProximity();
        maybeConsumeKeycardsAfterDoor();
    });
}
